/**
 * Utility functions for client-side sorting of Shopify data
 */

// Helper to get the lowest price from item pricing for sorting
export const getLowestPrice = (item: any): number => {
  // Handle Shopify items which have a different structure
  if (item && typeof item.price === 'number') {
    return item.price;
  }
  
  // Handle legacy item structure
  if (!item || !item.item_pricing || !Array.isArray(item.item_pricing)) {
    return 0;
  }

  const prices = item.item_pricing
    .filter((pricing: any) => pricing.price && pricing.price > 0)
    .map((pricing: any) => pricing.price);

  if (prices.length === 0) {
    return 0;
  }

  return Math.min(...prices);
};

// Helper to get the highest price from item pricing for sorting
export const getHighestPrice = (item: any): number => {
  // Handle Shopify items which have a different structure
  if (item && typeof item.price === 'number') {
    return item.price;
  }
  
  // Handle legacy item structure
  if (!item || !item.item_pricing || !Array.isArray(item.item_pricing)) {
    return 0;
  }

  const prices = item.item_pricing
    .filter((pricing: any) => pricing.price && pricing.price > 0)
    .map((pricing: any) => pricing.price);

  if (prices.length === 0) {
    return 0;
  }

  return Math.max(...prices);
};

// Helper to determine if an item is on special
export const isOnSpecial = (item: any): boolean => {
  // The transformShopifyProductToItemData function explicitly sets the isSpecial property
  // from Shopify metafields, so we should prioritize this
  if (item.isSpecial !== undefined) {
    return Boolean(item.isSpecial); // Convert to boolean to handle any truthy/falsy values
  }
  
  // Check item pricing for legacy items
  if (item.item_pricing && Array.isArray(item.item_pricing)) {
    return item.item_pricing.some((pricing: any) => pricing.special);
  }
  
  return false;
};

// Helper to determine if an item is half price
export const isHalfPrice = (item: any): boolean => {
  // The transformShopifyProductToItemData function explicitly sets the isHalfThePrice property
  // from Shopify metafields, so we should prioritize this
  if (item.isHalfThePrice !== undefined) {
    return Boolean(item.isHalfThePrice); // Convert to boolean to handle any truthy/falsy values
  }
  
  // For legacy items, check if special price is at least half off
  if (isOnSpecial(item) && item.item_pricing && Array.isArray(item.item_pricing)) {
    const special = item.item_pricing.find((pricing: any) => pricing.special);
    if (special && special.was_price) {
      return special.price <= special.was_price / 2;
    }
  }
  
  return false;
};

// Calculate the biggest discount amount for an item (either Coles or Woolworths)
export const getBiggestDiscountAmount = (item: any): number => {
  // Special case: if either isSpecial or isHalfPrice is defined, we might have direct discount info
  if (item.isSpecial || item.isHalfPrice) {
    // Check pricing info for both vendors
    if (item.item_pricing && Array.isArray(item.item_pricing)) {
      let maxDiscount = 0;
      
      // Check each vendor's pricing
      item.item_pricing.forEach((pricing: any) => {
        // If this pricing has a special flag and a was_price
        if (pricing.special && pricing.was_price && pricing.price) {
          const discountAmount = pricing.was_price - pricing.price;
          if (discountAmount > maxDiscount) {
            maxDiscount = discountAmount;
            
            // Store which vendor offers the biggest discount
            item._discountVendor = pricing.vendor_id === 1 ? 'Coles' : 'Woolworths';
            item._discountOriginal = pricing.was_price;
            item._discountCurrent = pricing.price;
          }
        }
      });
      
      if (maxDiscount > 0) {
        // Log significant discounts
        console.log(`DISCOUNT for ${item.name}: Original=$${item._discountOriginal?.toFixed(2)}, ` +
          `Current=$${item._discountCurrent?.toFixed(2)}, Savings=$${maxDiscount.toFixed(2)}`);
        
        // Store the discount amount for sorting
        item._discountAmount = maxDiscount;
        return maxDiscount;
      }
    }
  }
  
  // Calculate price difference between vendors as a fallback
  // Check if item has pricing from both vendors
  if (!item || !item.item_pricing || !Array.isArray(item.item_pricing)) {
    return 0;
  }
  
  // Find Coles and Woolworths pricing
  const colesPricing = item.item_pricing.find((p: any) => p.vendor_id === 1);
  const woolworthsPricing = item.item_pricing.find((p: any) => p.vendor_id === 2);
  
  // If we don't have both vendors' pricing, return 0
  if (!colesPricing || !woolworthsPricing) {
    return 0;
  }
  
  // Get prices or default to 0 if missing
  const colesPrice = colesPricing.price || 0;
  const woolworthsPrice = woolworthsPricing.price || 0;
  
  // If either price is zero (N/A), no valid comparison
  if (colesPrice <= 0 || woolworthsPrice <= 0) {
    return 0;
  }
  
  // Calculate the price difference between vendors
  const priceDifference = Math.abs(colesPrice - woolworthsPrice);
  
  // Store the vendor difference as a fallback discount amount
  item._vendorDiff = priceDifference;
  item._vendorDiffPercentage = ((priceDifference / Math.min(colesPrice, woolworthsPrice)) * 100);
  
  // Lower priority than actual discounts, so return a smaller value
  return priceDifference / 2; // Make vendor price differences worth half as much as actual discounts
};

// Get savings score for "Biggest Savings" sort - based on discount amount in dollars
export const getSavingsScore = (item: any): number => {
  // Calculate the biggest discount amount (original price - discounted price)
  const discountAmount = getBiggestDiscountAmount(item);
  
  // We're now using the raw discount amount in dollars as our primary sort criterion
  // We'll still use the special/half price flags as tiebreakers within the same discount amount
  
  // Store the discount amount for sorting
  item._discountAmount = discountAmount;
  
  // Return the discount amount directly - bigger discounts = higher score
  return discountAmount;
};

// Calculate discount percentage for an item 
export const calculateDiscountPercent = (item: any): number => {
  // If we have stored the original and current prices, calculate the percentage
  if (item._discountOriginal && item._discountCurrent && item._discountOriginal > 0) {
    return 100 * (1 - (item._discountCurrent / item._discountOriginal));
  }
  
  // For half price items (confirmed by metadata), we know it's 50% off
  if (isHalfPrice(item)) {
    return 50;
  }
  
  // For items flagged as special by metadata but not half price
  if (isOnSpecial(item) && !isHalfPrice(item)) {
    // Try to get the actual discount from pricing if available
    if (item.item_pricing && Array.isArray(item.item_pricing)) {
      const special = item.item_pricing.find((pricing: any) => pricing.special);
      if (special && special.was_price && special.was_price > 0) {
        return 100 * (1 - (special.price / special.was_price));
      }
    }
    // Default to 20% if we can't calculate exact discount
    return 20;
  }
  
  // For items with price difference between vendors, use the percentage difference
  if (item._vendorDiffPercentage) {
    return item._vendorDiffPercentage;
  }
  
  return 0; // No discount
};

// Helper to check if a product has valid pricing for both vendors
export const hasBothVendorPricing = (item: any): boolean => {
  if (!item || !item.item_pricing || !Array.isArray(item.item_pricing)) {
    return false;
  }

  // Check if we have pricing for both Coles (vendor_id=1) and Woolworths (vendor_id=2)
  const hasColesPrice = item.item_pricing.some((pricing: any) => 
    pricing.vendor_id === 1 && pricing.price && pricing.price > 0
  );
  
  const hasWoolworthsPrice = item.item_pricing.some((pricing: any) => 
    pricing.vendor_id === 2 && pricing.price && pricing.price > 0
  );
  
  return hasColesPrice && hasWoolworthsPrice;
};

// Helper to check if a product has at least one valid price
export const hasAnyValidPrice = (item: any): boolean => {
  if (!item || !item.item_pricing || !Array.isArray(item.item_pricing)) {
    return false;
  }

  return item.item_pricing.some((pricing: any) => pricing.price && pricing.price > 0);
};

// Helper to get the priority score for an item (used for default sorting)
export const getItemPriorityScore = (item: any): number => {
  // Priority 1 (highest): Items with pricing for both vendors
  if (hasBothVendorPricing(item)) {
    return 4;
  }
  
  // Priority 2: Items with both special AND half price indicators
  if (isHalfPrice(item) && isOnSpecial(item)) {
    return 3;
  }
  
  // Priority 3: Items with half price indicator only
  if (isHalfPrice(item)) {
    return 2;
  }
  
  // Priority 4: Items with special indicator only
  if (isOnSpecial(item)) {
    return 1;
  }
  
  // Priority 5 (lowest): Normal items with at least one valid price
  if (hasAnyValidPrice(item)) {
    return 0;
  }
  
  // Items with no valid prices should be filtered out
  return -1;
};

// Main sorting function used by both `sortItemsOnApi` and the reducer
export const sortItems = (items: any[], sortType: string): any[] => {
  // Make a copy of the array to avoid mutating the original
  let itemsCopy = [...items];
  
  // Filter out products with $0 price for both vendors
  itemsCopy = itemsCopy.filter(item => hasAnyValidPrice(item));
  
  // If MANUAL sort is specified, return items exactly as they are (after filtering $0 prices)
  // This preserves the order received from Shopify for collection products
  if (sortType === "MANUAL") {
    console.log('Using MANUAL sort order from Shopify - preserving original order');
    return itemsCopy;
  }
  
  // Apply sorting based on the sort type
  switch (sortType) {
    case "A-Z":
      // First sort by priority score, then by name
      return itemsCopy.sort((a, b) => {
        const priorityA = getItemPriorityScore(a);
        const priorityB = getItemPriorityScore(b);
        
        // First sort by priority (higher priority first)
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // Then sort by name
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return nameA.localeCompare(nameB);
      });

    case "Z-A":
      // First sort by priority score, then by name in reverse
      return itemsCopy.sort((a, b) => {
        const priorityA = getItemPriorityScore(a);
        const priorityB = getItemPriorityScore(b);
        
        // First sort by priority (higher priority first)
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // Then sort by name in reverse
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return nameB.localeCompare(nameA);
      });

    case "HighestPrice":
      // First sort by priority score, then by highest price
      return itemsCopy.sort((a, b) => {
        const priorityA = getItemPriorityScore(a);
        const priorityB = getItemPriorityScore(b);
        
        // First sort by priority (higher priority first)
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // Then sort by highest price
        const priceA = getHighestPrice(a);
        const priceB = getHighestPrice(b);
        
        // Handle the case where both prices are 0
        if (priceA === 0 && priceB === 0) return 0;
        // Items with price 0 (N/A) should be at the end
        if (priceA === 0) return 1;
        if (priceB === 0) return -1;
        
        return priceB - priceA;
      });

    case "LowestPrice":
      // First sort by priority score, then by lowest price
      return itemsCopy.sort((a, b) => {
        const priorityA = getItemPriorityScore(a);
        const priorityB = getItemPriorityScore(b);
        
        // First sort by priority (higher priority first)
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // Then sort by lowest price
        const priceA = getLowestPrice(a);
        const priceB = getLowestPrice(b);
        
        // Handle the case where both prices are 0
        if (priceA === 0 && priceB === 0) return 0;
        // Items with price 0 (N/A) should be at the end
        if (priceA === 0) return 1;
        if (priceB === 0) return -1;
        
        return priceA - priceB;
      });

    case "Difference":
      // IMPORTANT: Sort debugging output - show all item info in one pass
      console.log('\n===== SORTING ITEMS BY BIGGEST SAVINGS ====');
      console.log(`Total items to sort: ${itemsCopy.length}`);
      
      // First pass to calculate and store scoring information for each item
      itemsCopy.forEach(item => {
        // Calculate the discount amount (original price - current price)
        // This does all our calculations in one go
        item._savingsScore = getSavingsScore(item);
        
        // Calculate discount percentage for secondary sorting
        item._discountPercent = calculateDiscountPercent(item);
        
        // Calculate priority score
        item._priorityScore = getItemPriorityScore(item);
        
        // Log info about items with meaningful discounts
        if (item._discountAmount && item._discountAmount > 0.5) {
          let message = `Item ${item.name}: Discount=$${(item._discountAmount || 0).toFixed(2)}`;  
          
          // Add vendor info if available
          if (item._discountVendor) {
            message += `, from ${item._discountVendor} (was $${item._discountOriginal.toFixed(2)}, now $${item._discountCurrent.toFixed(2)})`;  
          }
          
          // Add vendor difference info if available
          if (item._vendorDiff && item._vendorDiff > 0.5) {
            message += `, Vendor diff=$${item._vendorDiff.toFixed(2)}`;  
          }
          
          console.log(message);
        }
      });
      
      return itemsCopy.sort((a, b) => {
        // First sort by priority score
        const priorityA = a._priorityScore !== undefined ? a._priorityScore : getItemPriorityScore(a);
        const priorityB = b._priorityScore !== undefined ? b._priorityScore : getItemPriorityScore(b);
        
        if (priorityA !== priorityB) {
          return priorityB - priorityA; // Higher priority first
        }
        
        // Then sort by discount amount (regardless of which vendor offers it)
        const discountA = a._discountAmount || 0;
        const discountB = b._discountAmount || 0;
        
        // If discount amounts are meaningfully different (more than 10 cents)
        if (Math.abs(discountA - discountB) > 0.1) {
          console.log(`Sorting by discount amount: ${a.name} ($${discountA.toFixed(2)}) vs ${b.name} ($${discountB.toFixed(2)})`);
          return discountB - discountA; // Higher discount first
        }
        
        // Secondary sort: if the discount amounts are similar, use discount percentage
        const percentA = a._discountPercent || 0;
        const percentB = b._discountPercent || 0;
        
        if (Math.abs(percentA - percentB) > 1) {
          console.log(`Sorting by discount percent: ${a.name} (${percentA.toFixed(1)}%) vs ${b.name} (${percentB.toFixed(1)}%)`);
          return percentB - percentA; // Higher percentage first
        }
        
        // Quaternary sort: by lowest price (most users prefer lower priced items when all else is equal)
        const priceA = getLowestPrice(a);
        const priceB = getLowestPrice(b);
        
        // Handle 0 prices
        if (priceA === 0 && priceB === 0) return 0;
        if (priceA === 0) return 1;
        if (priceB === 0) return -1;
        
        return priceA - priceB;
      });

    default:
      // Default sorting: by priority score, then by name
      return itemsCopy.sort((a, b) => {
        const priorityA = getItemPriorityScore(a);
        const priorityB = getItemPriorityScore(b);
        
        // First sort by priority (higher priority first)
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // Then sort by name
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }
};
